<template>
    <div
        class="relative flex flex-col items-center text-center max-w-2xl mx-auto"
    >
        <div
            v-if="showConfetti"
            class="fixed inset-0 overflow-hidden pointer-events-none z-50"
        >
            <span
                v-for="(piece, index) in confettiPieces"
                :key="index"
                class="absolute -top-8 animate-confetti"
                :class="piece.color"
                :style="{
                    left: piece.left,
                    width: piece.width,
                    height: piece.height,
                    borderRadius: piece.radius,
                    opacity: piece.opacity,
                    animationDelay: piece.delay,
                    animationDuration: piece.duration,
                    transform: `rotate(${piece.rotate}deg)`,
                    '--drift': piece.drift
                }"
            ></span>
        </div>

        <div
            class="w-16 h-16 rounded-full bg-brand-blue flex items-center justify-center"
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                width="32"
                height="32"
                fill="currentColor"
                class="text-white"
            >
                <path
                    fill-rule="evenodd"
                    d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                    clip-rule="evenodd"
                />
            </svg>
        </div>

        <h1 class="text-3xl font-bold text-theme-base mt-6">
            ¡Felicitaciones! Tu catálogo está listo
        </h1>
        <p v-if="isTrial" class="text-theme-muted mt-3">
            Terminaste de configurar tu catálogo. Ahora tienes
            {{ trialDays }} días de prueba gratis para explorar todas las
            funciones y hacer crecer tu negocio.
        </p>
        <p v-else class="text-theme-muted mt-3">
            Terminaste de configurar tu catálogo. Ya puedes empezar a usar
            tucatalogo.co.
        </p>

        <div
            v-if="catalogUrl"
            class="mt-6 w-full bg-theme-card border border-theme-border rounded-xl p-5"
        >
            <div
                class="flex flex-col items-center gap-4 sm:flex-row sm:items-center"
            >
                <div
                    v-if="showQr"
                    class="flex-shrink-0 rounded-lg bg-white p-2"
                >
                    <qr-viewer :value="catalogUrl" :size="160" />
                </div>
                <div
                    class="flex min-w-0 flex-col items-center gap-3 sm:items-start sm:text-left"
                >
                    <p class="text-sm text-theme-muted">
                        Tu catálogo ya está en línea
                    </p>
                    <a
                        :href="catalogUrl"
                        target="_blank"
                        rel="noopener"
                        class="text-theme-primary font-mono text-lg break-all hover:underline"
                    >
                        {{ catalogLabel }}
                    </a>
                    <app-button v-if="showQr" border @click="downloadQr">
                        <span class="flex items-center gap-2">
                            <qr-code-icon class="h-4 w-4" />
                            Descargar QR
                        </span>
                    </app-button>
                </div>
            </div>
        </div>

        <div class="mt-6 w-full">
            <p class="text-sm text-theme-muted mb-3 text-left">
                Así se ve tu catálogo
            </p>
            <catalog-preview-card :show-qr-button="false" />
        </div>

        <div
            v-if="planCode"
            class="mt-6 w-full bg-theme-card border border-theme-border rounded-xl p-5 text-left"
        >
            <p class="text-theme-base font-medium mb-3">
                Esto es lo que puedes hacer con tu plan {{ planDisplayName }}
            </p>
            <ul class="space-y-2.5 text-sm text-theme-muted">
                <li class="flex items-start gap-2">
                    <span>🛍️</span>
                    <span v-if="catalogMode === 'checkout'">
                        Comparte el link de tu catálogo por WhatsApp o tus redes
                        sociales y recibe pedidos directo de tus clientes.
                    </span>
                    <span v-else-if="catalogMode === 'catalog'">
                        Comparte el link de tu catálogo por WhatsApp o tus redes
                        sociales para que tus clientes vean tus productos y te
                        escriban para comprar.
                    </span>
                    <span v-else>
                        Comparte el link de tu perfil por WhatsApp o tus redes
                        sociales para que tus clientes te encuentren y te
                        contacten.
                    </span>
                </li>
                <li v-if="hasSalesTools" class="flex items-start gap-2">
                    <span>💰</span>
                    <span>
                        Registra
                        <strong class="text-theme-base">
                            ventas internas
                        </strong>
                        o crea
                        <strong class="text-theme-base">cotizaciones</strong>
                        desde el panel (sección Ventas), aunque el cliente no
                        compre por el catálogo.
                    </span>
                </li>
                <li class="flex items-start gap-2">
                    <span>🎨</span>
                    <span>
                        Vuelve a Configuración cuando quieras para ajustar
                        marca, tema, redes y enlaces sin perder lo que ya
                        armaste.
                    </span>
                </li>
                <li v-if="hasSalesTools" class="flex items-start gap-2">
                    <span>📊</span>
                    <span>
                        Consulta el historial de ventas y el estado de tus
                        pedidos en tiempo real desde el mismo panel.
                    </span>
                </li>
                <li
                    v-for="line in planMarketingLines"
                    :key="line"
                    class="flex items-start gap-2"
                >
                    <span>{{ line }}</span>
                </li>
            </ul>

            <ul
                v-if="planLimitLines.length"
                class="space-y-1 text-sm text-theme-muted mt-4 pt-4 border-t border-theme-border"
            >
                <li v-for="line in planLimitLines" :key="line.text">
                    <button
                        type="button"
                        class="flex w-full items-start justify-between gap-2 py-1 text-left"
                        :class="line.detail ? '' : 'cursor-default'"
                        @click="line.detail && toggleFeature(line.text)"
                    >
                        <span class="flex items-start gap-2">
                            <span class="text-theme-success">✓</span>
                            <span>{{ line.text }}</span>
                        </span>
                        <chevron-down-icon
                            v-if="line.detail && isFeatureOpen(line.text)"
                            class="w-4 h-4 flex-shrink-0"
                        />
                        <chevron-right-icon
                            v-else-if="line.detail"
                            class="w-4 h-4 flex-shrink-0"
                        />
                    </button>
                    <custom-collapse
                        v-if="line.detail"
                        :visible="isFeatureOpen(line.text)"
                    >
                        <p class="text-xs pl-6 pb-1.5">
                            {{ line.detail }}
                        </p>
                    </custom-collapse>
                </li>
            </ul>
        </div>

        <div
            v-if="isTrial"
            class="mt-6 w-full bg-theme-card border border-theme-border rounded-xl p-5 text-left"
        >
            <p class="text-theme-base font-medium">
                ¿Qué pasa cuando termine tu prueba gratis?
            </p>
            <p v-if="isWithoutProducts" class="text-theme-muted text-sm mt-2">
                Al finalizar los 4 meses podrás seguir usando tucatalogo.co para
                compartir tus enlaces y contenido, pero con funciones limitadas
                de la plataforma. Para desbloquear todas las funciones
                necesitarás contratar una suscripción.
            </p>
            <p v-else class="text-theme-muted text-sm mt-2">
                Al finalizar los 4 meses pasas al plan Free: quedan publicados
                tus 3 primeros productos y el resto se oculta, sin borrarse, y
                otras herramientas de la plataforma quedan restringidas. Para
                volver a publicar todo tu catálogo necesitarás contratar una
                suscripción.
            </p>
        </div>

        <button
            class="w-full mt-10 py-3 rounded-full font-medium transition bg-brand-blue text-white hover:opacity-90"
            @click="goToApp"
        >
            Ir al portal
        </button>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

import { IS_ENABLED_CATALOG_QR } from "@/common/constants";
import { BUSINESS_TYPES_WITHOUT_PRODUCTS } from "@/common/fields/business-types";
import {
    PLAN_CATALOG_CHECKOUT,
    buildPlanLimitLines,
    buildPlanMarketingLines
} from "@/common/fields/plan-content";
import { buildQrFilename, downloadQrImage } from "@/common/lib/qr";

import CatalogPreviewCard from "@/components/editor/CatalogPreviewCard.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import QrCodeIcon from "@/components/icons/QrCodeIcon.vue";
import CustomCollapse from "@/components/utils/CustomCollapse.vue";
import QrViewer from "@/components/utils/QrViewer.vue";

const CONFETTI_COLORS = [
    "bg-brand-blue",
    "bg-brand-red",
    "bg-brand-magenta",
    "bg-brand-green"
];

export default {
    name: "OnboardingWelcomePage",

    components: {
        CatalogPreviewCard,
        ChevronDownIcon,
        ChevronRightIcon,
        CustomCollapse,
        QrCodeIcon,
        QrViewer
    },

    data() {
        return {
            showConfetti: true,
            openFeatureTexts: [],
            planCode: null,
            planDisplayName: "",
            planMarketingLines: [],
            planLimitLines: [],
            isTrial: false,
            trialDays: 0,
            confettiPieces: Array.from({ length: 120 }, () => ({
                left: `${Math.random() * 100}%`,
                color: CONFETTI_COLORS[
                    Math.floor(Math.random() * CONFETTI_COLORS.length)
                ],
                delay: `${Math.random() * 1.2}s`,
                duration: `${2 + Math.random() * 2.5}s`,
                rotate: Math.random() * 360,
                width: `${4 + Math.random() * 8}px`,
                height: `${6 + Math.random() * 12}px`,
                drift: `${-200 + Math.random() * 400}px`,
                opacity: 0.7 + Math.random() * 0.3,
                radius: Math.random() > 0.6 ? "999px" : "2px"
            }))
        };
    },

    computed: {
        ...mapState("onboarding", ["businessType", "selections"]),
        ...mapState("auth", ["tenantSlug", "tenantId"]),
        catalogUrl() {
            return this.$publicUrl;
        },
        showQr() {
            return IS_ENABLED_CATALOG_QR && Boolean(this.catalogUrl);
        },
        catalogLabel() {
            return this.$publicUrl.replace(/^https?:\/\//, "");
        },
        isWithoutProducts() {
            return BUSINESS_TYPES_WITHOUT_PRODUCTS.includes(
                this.selections.businessType || this.businessType
            );
        },
        // "checkout": el cliente arma y envía el pedido sin salir del
        // catálogo (Pro/Premium). "catalog": tiene catálogo de productos
        // pero cierra la venta por fuera, ej. WhatsApp (Free con hasta 3
        // productos, Starter). "profile": solo perfil de enlaces (tipos de
        // negocio sin productos, o plan aún sin resolver).
        catalogMode() {
            if (!this.planCode) return "profile";
            if (PLAN_CATALOG_CHECKOUT[this.planCode]) return "checkout";
            return this.isWithoutProducts ? "profile" : "catalog";
        },
        // Ventas internas y cotizaciones son Starter en adelante (ver stage
        // de Starter en PlanGuideModal / PlanGuideSection.vue del landing:
        // "Ya registras ventas y clientes y mandas cotizaciones en PDF").
        hasSalesTools() {
            return Boolean(this.planCode) && this.planCode !== "free";
        }
    },

    async mounted() {
        this.$sound.play();
        this.$analytics.trackEvent("onboarding_completed", {
            onboarding_type: "multitenant"
        });
        this.$analytics.trackSignUp(this.tenantId);
        try {
            await this.getOnboardingStatus();
        } catch {
            // Keep local state if status endpoint is unavailable
        }
        await this.loadPlanDetails();
    },

    methods: {
        async downloadQr() {
            try {
                await downloadQrImage(
                    this.catalogUrl,
                    buildQrFilename(this.catalogUrl, this.tenantSlug)
                );
            } catch (error) {
                this.$swal.fire({
                    icon: "error",
                    title: "No pudimos generar la imagen",
                    text: error?.message || "Intenta de nuevo en unos segundos."
                });
            }
        },
        ...mapActions("onboarding", [
            "getOnboardingStatus",
            "getOnboardingPlans"
        ]),
        ...mapActions("platformSubscriptions", ["getMySubscription"]),
        goToApp() {
            this.$router.push("/app");
        },

        toggleFeature(text) {
            const index = this.openFeatureTexts.indexOf(text);
            if (index === -1) {
                this.openFeatureTexts.push(text);
            } else {
                this.openFeatureTexts.splice(index, 1);
            }
        },

        isFeatureOpen(text) {
            return this.openFeatureTexts.includes(text);
        },

        // Solo enriquece la pantalla si se puede resolver el plan elegido —
        // si algo falla, se queda con el contenido genérico de arriba en vez
        // de romper la celebración de fin de onboarding.
        async loadPlanDetails() {
            try {
                const subscription = await this.getMySubscription();
                this.isTrial = subscription?.current?.type === "trial";
                const code = subscription?.current?.planName?.toLowerCase();
                if (!code) return;
                const { items } = await this.getOnboardingPlans();
                const plan = items.find((item) => item.code === code);
                if (!plan) return;
                this.planCode = code;
                this.planDisplayName = plan.name;
                this.trialDays = plan.trialDays || 0;
                this.planMarketingLines = buildPlanMarketingLines(plan, {
                    isTrial: this.isTrial
                });
                this.planLimitLines = buildPlanLimitLines(plan);
            } catch {
                // Sin detalle de plan: se queda con el contenido genérico
            }
        }
    }
};
</script>
